// --- Core ---
import {useEffect} from "react";
import {createRoot} from "react-dom/client";
import {FormattedMessage} from "react-intl";
import * as Sentry from "@sentry/react";
import {sentryCreateBrowserRouter} from "src/config/sentry";
import "src/fonts/fonts.css";
import {Provider} from "react-redux";
import {Link, Outlet, RouterProvider, useNavigate, useRouteError} from "react-router";
// --- SaniQ ---
import {currentTenant} from "helpers/Application";
import {language, messages} from './config/i18n';
import {corporateIdentity} from "src/config/corporateIdentity";
import {store} from "store";
import {SaniQErrorBoundary} from "./modules/app/hoc/InnerErrorBoundary";
import TenantLoginLayout from "./modules/tenant_login/layout/TenantLoginLayout";

interface ApplicationError {
  status?: number;
}

type RouteError = ApplicationError & Error;

//FIXME(Keune): When the router is inside the IntlProvider, this is not needed anymore
const FallbackComponent = () => {
  const navigate = useNavigate();
  //NOTE(Keune): Thif fallback also catches when a route is not found
  const error = useRouteError() as RouteError;

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const error_text = `\n\nFehlermeldung:\n\n${error.stack}`;
  const link_target = `mailto:support@qurasoft.de?subject=SaniQ%20Praxis%20Fehlermeldung&body=${encodeURIComponent(error_text)}`;

  if (error.status === 404) {
    /* eslint-disable formatjs/no-literal-string-in-jsx -- This component is outside the translation provider (for now) */
    return (
      <div className="loading">
        <img className="saniq-logo" alt="SaniQ Logo"/>
        <h1>Adresse nicht gefunden</h1>
        <p>Sie haben einen nicht existierenden Pfad in SaniQ OS gefunden.</p>
        <p>Wenn Sie diesen Fehler über die Plattform erreicht haben, wenden Sie sich bitte an <a href={link_target}> unsere Support E-Mail</a>.</p>
        <Link
          to=".."
          onClick={
            (e) => {
              e.preventDefault();
              navigate(-1);
            }
          }
        >
          Zurück zur Anwendung
        </Link>
      </div>
    );
    /* eslint-enable formatjs/no-literal-string-in-jsx */
  }

  /* eslint-disable formatjs/no-literal-string-in-jsx -- This component is outside the translation provider (for now) */
  //NOTE(Keune): Use the styles from the initial index.html
  return (
    <div className="loading">
      <img className="saniq-logo" alt="SaniQ Logo"/>
      <h1>Uh oh!</h1>
      <p>In SaniQ OS ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut. :(</p>
      <p>
        Wenn Sie diesen Fehler wiederholt sehen, wenden Sie sich bitte an <a href={link_target}> unsere Support E-Mail</a>.
      </p>
      <p>
        <small>
          Alternativ wenden Sie sich an uns über das <a href="https://saniq.org/kontakt/" target="_blank" rel="noreferrer">Kontaktformular</a> mit
          Angabe des unten aufgeführten Fehlertextes.
        </small>
      </p>
      <br/>
      <h2>Fehlertext</h2>
      <div style={{borderStyle: "solid", borderWidth: "1px", borderColor: "#fff", width: "50vw"}}>
        <p>{`${error.name}: ${error.message}`}</p>
        <pre>{error.stack}</pre>
      </div>
    </div>
  );
  /* eslint-enable formatjs/no-literal-string-in-jsx */
};

// Render the main component into the dom
const container = document.getElementById('app');
const root = createRoot(container);
const tenant = currentTenant();

const router = sentryCreateBrowserRouter([
  {
    path: "/",
    loader: async () => {
      const locale = await language();
      const ci = await corporateIdentity();
      const initialMessages = await messages(locale);

      return {ci, initialMessages, initialLocale: locale};
    },
    lazy: () => import("src/modules/app/container/EntryPoint"),
    ErrorBoundary: FallbackComponent,
    children: [
      tenant === "login" &&
      {
        path: "candidates",
        lazy: () => import("src/modules/tenant_login/container/TenantLogins")
      },
      tenant === "manage" &&
      {
        path: "manage",
        lazy: () => import("./modules/manage/layout/ManageLayout"),
        children: [
          {
            path: "dashboard",
            lazy: () => import("src/modules/manage/container/Dashboard")
          },
          {
            path: "licenses",
            lazy: () => import("src/modules/manage/modules/licenses/container/Licenses"),
            children: [
              {
                path: ":licenseId",
                lazy: () => import("src/modules/manage/modules/licenses/container/LicenseDialog")
              }
            ]
          },
          {
            path: "logistics/sensor_packages/:sensorPackageId",
            lazy: () => import("src/modules/manage/container/logistic/SensorPackage")
          },
          {
            path: "logistics/articles/:articleId/stock",
            lazy: () => import("src/modules/manage/container/logistic/OrderArticleStockDialog")
          },
          {
            path: "logistics/articles/:articleId",
            lazy: () => import("src/modules/manage/container/logistic/OrderArticle")
          },
          {
            path: "logistics",
            lazy: () => import("src/modules/manage/container/Logistics"),
            children: [
              {
                path: "articles",
                lazy: () => import("src/modules/manage/container/logistic/OrderArticles")
              },
              {
                path: "devices",
                lazy: () => import("src/modules/manage/container/logistic/ConnectedDevices")
              },
              {
                path: "devices/medisantes/:deviceId",
                lazy: () => import("src/modules/manage/container/logistic/device/MedisanteDeviceDetail")
              },
              {
                path: "orders",
                lazy: () => import("src/modules/manage/container/logistic/Orders"),
                children: [
                  {
                    path: "new",
                    lazy: () => import("src/modules/manage/container/logistic/OrderDialogFormNew")
                  }
                ]
              },
              {
                path: "sensor_packages",
                lazy: () => import("src/modules/manage/container/logistic/SensorPackages")
              }
            ]
          },
          {
            path: "tenants",
            lazy: () => import("src/modules/manage/container/Tenants"),
            children: [
              {
                path: "new",
                lazy: () => import("src/modules/manage/container/TenantDialogNewForm")
              }
            ]
          },
          {
            path: "tenants/:tenantId",
            lazy: () => import("./modules/manage/container/TenantDetail"),
            children: [
              {
                path: "editor",
                lazy: () => import("./modules/manage/container/TenantEditor")
              },
              {
                path: "corporate_identity",
                lazy: () => import("./modules/manage/container/IdentityEditor")
              },
              {
                path: "configuration",
                lazy: () => import("./modules/manage/container/ConfigurationEditor")
              },
              {
                path: "surveys",
                lazy: () => import("./modules/manage/container/TenantSurveysEditor")
              },
              {
                path: "mails",
                lazy: () => import("./modules/manage/container/MailTemplateEditor")
              },
              {
                path: "physicians",
                lazy: () => import("./modules/manage/container/TenantPhysicians")
              }
            ]
          }
        ]
      },
      tenant === "meeting" &&
      {
        path: "conference/login",
        lazy: () => import("./modules/video_consultation/containers/GuestLogin")
      },
      tenant === "meeting" &&
      {
        path: "conference/session",
        lazy: () => import("./modules/video_consultation/containers/GuestSession")
      },
      {
        path: "app/password/reset/edit",
        lazy: () => import("src/modules/password/modules/app/container/AppPasswordResetForm")
      },
      {
        path: "login",
        element: <TenantLoginLayout><Outlet/></TenantLoginLayout>,
        children: [
          {
            path: "sign_in",
            lazy: () => import("src/modules/login/containers/LoginProvider")
          },
          {
            path: "microsoft",
            lazy: () => import("src/modules/login/containers/MicrosoftLoginProvider")
          },
          {
            path: "otp",
            lazy: () => import("src/modules/otp/containers/OtpForm")
          }
        ]
      },
      {
        path: "password",
        lazy: () => import("src/modules/password/layout/PasswordLayout"),
        children: [
          {
            path: "new",
            lazy: () => import("src/modules/password/container/PasswordNew")
          },
          {
            path: "reset/edit",
            lazy: () => import("src/modules/password/container/PasswordEdit")
          },
          {
            path: "reset",
            lazy: () => import("src/modules/password/container/PasswordReset")
          }
        ]
      },
      {
        path: "protected",
        lazy: () => import("src/modules/app/container/ProtectedApplication"),
        children: [
          {
            path: "content",
            lazy: () => import("src/modules/system/layout/ContentLayout"),
            children: [
              {
                path: "user/:id",
                lazy: () => import("src/modules/system/modules/users/container/UserFormDialog")
              },
              {
                path: "event_schemas",
                lazy: () => import("src/modules/event_schema/container/EventSchemas"),
                children: [
                  {
                    path: ":id",
                    lazy: () => import("src/modules/event_schema/container/EventSchemaForm")
                  }
                ]
              },
              {
                path: 'trigger_templates',
                lazy: () => import("src/modules/system/modules/trigger_templates/container/TriggerTemplateGroups"),
                children: [
                  {
                    path: ":id",
                    lazy: () => import("./modules/system/modules/trigger_templates/container/TriggerTemplateGroupDialog")
                  }
                ]
              },
              {
                path: "cohorts",
                lazy: () => import("src/modules/system/modules/cohorts/container/Cohorts"),
                children: [
                  {
                    path: ":id",
                    lazy: () => import("src/modules/system/modules/cohorts/container/CohortForm")
                  }
                ]
              },
              {
                path: "drugs",
                lazy: () => import("src/modules/system/modules/drugs/container/Drugs"),
                children: [
                  {
                    path: ":id",
                    lazy: () => import("src/modules/system/modules/drugs/container/DrugForm")
                  }
                ]
              },
              {
                path: "feeling_factors",
                lazy: () => import("src/modules/system/modules/feeling_factors/container/FeelingFactors"),
                children: [
                  {
                    path: ":id",
                    lazy: () => import("src/modules/system/modules/feeling_factors/container/FeelingFactorForm")
                  }
                ]
              },
              {
                path: "health_provider",
                lazy: () => import("src/modules/system/modules/health_providers/container/HealthProviders"),
                children: [
                  {
                    path: "institution",
                    handle: {type: "institution"},
                    lazy: () => import("src/modules/system/modules/health_providers/container/HealthProviderFromIdentity")
                  },
                  {
                    path: "person",
                    handle: {type: "person"},
                    lazy: () => import("src/modules/system/modules/health_providers/container/HealthProviderFromIdentity")
                  },
                  {
                    path: ":id",
                    lazy: () => import("src/modules/system/modules/health_providers/components/HealthProviderForm")
                  }
                ]
              },
              {
                path: "media_library",
                lazy: () => import("src/modules/system/modules/media_library/container/MediaLibrary"),
                children: [
                  {
                    path: ":id",
                    lazy: () => import("src/modules/system/modules/media_library/container/MediumForm")
                  }
                ]
              },
              {
                path: "medical_billings",
                lazy: () => import("src/modules/medical_billings/component/MedicalBillings"),
                children: [
                  {
                    path: ":id",
                    lazy: () => import("src/modules/medical_billings/component/MedicalBillingForm")
                  }
                ]
              },
              {
                path: "people/institutions",
                lazy: () => import("src/modules/system/modules/people/container/Institutions"),
                children: [
                  {
                    path: ":id",
                    lazy: () => import("src/modules/system/modules/people/container/InstitutionForm")
                  }
                ]
              },
              {
                path: "people",
                lazy: () => import("src/modules/system/modules/people/container/People"),
                children: [
                  {
                    path: ":id",
                    lazy: () => import("src/modules/system/modules/people/container/PersonForm")
                  }
                ]
              },
              {
                path: "programs",
                lazy: () => import("src/modules/system/modules/programs/container/Programs"),
                children: [
                  {
                    path: ":id",
                    lazy: () => import("src/modules/system/modules/programs/container/ProgramForm")
                  }
                ]
              },
              {
                path: "programs/components",
                lazy: () => import("src/modules/system/modules/programs/modules/components/container/Components"),
                children: [
                  {
                    //TODO(Keune): Better handling of nested routes?
                    path: ":id",
                    lazy: () => import("src/modules/system/modules/programs/modules/components/container/ComponentForm"),
                    children: [
                      {
                        path: "task/:taskId",
                        lazy: () => import("src/modules/tasks/container/TaskForm")
                      },
                      {
                        path: "goal/:goalId",
                        lazy: () => import("src/modules/patients/modules/programs/modules/goals/container/GoalForm")
                      }
                    ]
                  }
                ]
              },
              {
                path: "studies",
                lazy: () => import("src/modules/system/modules/studies/container/Studies"),
                children: [
                  {
                    path: ":id",
                    lazy: () => import("src/modules/system/modules/studies/container/Study")
                  }
                ]
              },
              {
                path: "surveys",
                lazy: () => import("src/modules/system/modules/surveys/container/Surveys"),
                children: [
                  {
                    path: ":id",
                    lazy: () => import("src/modules/system/modules/surveys/container/SurveyForm"),
                    children: [
                      {
                        path: "goal/:goalId",
                        lazy: () => import("src/modules/patients/modules/programs/modules/goals/container/GoalForm")
                      },
                      {
                        path: "medium/:mediumId",
                        lazy: () => import("src/modules/system/modules/surveys/container/SimpleMediumForm")
                      },
                      {
                        path: "survey/:surveyId",
                        lazy: () => import("src/modules/system/modules/surveys/container/SimpleSurveyForm")
                      },
                      {
                        path: "task/:taskId",
                        lazy: () => import("src/modules/tasks/container/TaskForm")
                      }
                    ]
                  }
                ]
              },
              {
                path: "text_templates",
                lazy: () => import("src/modules/system/modules/text_templates/components/TextTemplates"),
                children: [
                  {
                    path: ":id",
                    lazy: () => import("src/modules/system/modules/text_templates/components/TextTemplateForm")
                  }
                ]
              }
            ]
          },
          {
            path: "dashboard",
            lazy: () => import("src/modules/dashboard/layout/Overview"),
            children: [
              {
                path: "user/:id",
                lazy: () => import("src/modules/system/modules/users/container/UserFormDialog")
              },
              {
                path: "insights",
                lazy: () => import("src/modules/dashboard/container/InsightsLayout")
              },
              {
                path: "alarms/adherence",
                lazy: () => import("src/modules/triggers/modules/container/AdherenceEvents")
              },
              {
                path: "alarms",
                lazy: () => import("src/modules/dashboard/layout/DashboardLayout"),
                children: [
                  {
                    path: "patients/:id/triggers/events",
                    lazy: () => import("src/modules/dashboard/modules/triggers/container/MeasurementEventsDialog"),
                    children: [
                      {
                        path: ":id/call_logging",
                        lazy: () => import("src/modules/call_logging/container/CallLoggerDialog")
                      },
                      {
                        path: "task/:taskId",
                        lazy: () => import("src/modules/tasks/container/TaskForm")
                      },
                      {
                        path: ":taskId/work",
                        lazy: () => import("src/modules/tasks/container/TaskWork")
                      },
                      {
                        path: "findings/:findingId",
                        lazy: () => import("src/modules/patients/modules/findings/container/FindingForm")
                      },
                      {
                        path: ":findingId/files/:fileId/preview",
                        lazy: () => import("src/modules/patients/modules/findings/container/FilePreview")
                      }
                    ]
                  }
                ]
              },
              {
                path: "groups",
                lazy: () => import("src/modules/dashboard/container/Groups")
              },
              {
                path: "incoming_ecgs",
                lazy: () => import("src/modules/dashboard/container/IncomingEcgs"),
                children: [
                  {
                    path: "patients/:id/ecgs/:ecgId",
                    lazy: () => import("src/modules/patients/modules/measurements/modules/ecg/container/ECGDetail"),
                    children: [
                      {
                        path: ":taskId/task",
                        lazy: () => import("src/modules/tasks/container/TaskForm")
                      }
                    ]
                  }
                ]
              },
              {
                path: "messages",
                lazy: () => import("src/modules/messages/container/Chat"),
                children: [
                  {
                    index: true,
                    lazy: () => import("src/modules/messages/container/EmptyChat")
                  },
                  {
                    path: ":id",
                    lazy: () => import("src/modules/messages/container/Messages"),
                    children: [
                      {
                        path: "measurements/:measurementId",
                        lazy: () => import("src/modules/patients/modules/measurements/container/MeasurementDetail")
                      }
                    ]
                  }
                ]
              },
              {
                path: "tasks/schedules",
                lazy: () => import("src/modules/tasks/layouts/TaskSchedulesWithPaperLayout"),
                children: [
                  {
                    path: ":taskId",
                    lazy: () => import("src/modules/tasks/container/TaskForm")
                  },
                  {
                    path: ":taskId/work",
                    lazy: () => import("src/modules/tasks/container/TaskWork")
                  },
                  {
                    path: "patient/:id/ecg/:ecgId",
                    lazy: () => import("src/modules/patients/modules/measurements/modules/ecg/container/ECGDetail"),
                    children: [
                      {
                        path: ":taskId/task",
                        lazy: () => import("src/modules/tasks/container/TaskForm")
                      }
                    ]
                  }
                ]
              },
              {
                path: "tasks",
                lazy: () => import("src/modules/tasks/layouts/TasksWithPaperLayout"),
                children: [
                  {
                    path: ":taskId",
                    lazy: () => import("src/modules/tasks/container/TaskForm")
                  },
                  {
                    path: ":taskId/work",
                    lazy: () => import("src/modules/tasks/container/TaskWork")
                  },
                  {
                    path: "bulk/assign",
                    lazy: () => import("src/modules/tasks/container/BulkAssign")
                  },
                  {
                    path: "patient/:id/ecg/:ecgId",
                    lazy: () => import("src/modules/patients/modules/measurements/modules/ecg/container/ECGDetail"),
                    children: [
                      {
                        path: ":taskId/task",
                        lazy: () => import("src/modules/tasks/container/TaskForm")
                      }
                    ]
                  }
                ]
              },
              {
                path: "video_consultations",
                lazy: () => import("src/modules/video_consultation/containers/VideoConsultations"),
                children: [
                  {
                    path: ":id",
                    lazy: () => import("src/modules/video_consultation/containers/VideoConsultationForm")
                  }
                ]
              },
              {
                path: "treatments",
                lazy: () => import("src/modules/dashboard/container/Treatments"),
                children: [
                  {
                    path: ":treatmentId/patients/:id",
                    lazy: () => import("src/modules/patients/modules/treatments/container/TreatmentForm")
                  }
                ]
              },
              {
                path: "visites",
                lazy: () => import("src/modules/dashboard/container/ScheduledVisites")
              }
            ]
          },
          {
            path: "interview_guides/:id",
            lazy: () => import("src/modules/interview_guides/Preview")
          },
          {
            path: "patients",
            ErrorBoundary: SaniQErrorBoundary,
            children: [
              {
                index: true,
                lazy: () => import("src/modules/patients/container/Patients")
              },
              {
                lazy: () => import("src/modules/patients/container/Patients"),
                children: [
                  {
                    path: "new",
                    lazy: () => import("src/modules/patients/container/CreatePatientForm")
                  },
                  {
                    path: "task/:taskId",
                    lazy: () => import("src/modules/tasks/container/TaskForm")
                  },
                  {
                    path: ":id/call_logging",
                    lazy: () => import("src/modules/call_logging/container/CallLoggerDialog")
                  },
                  {
                    path: ":patientId/inactive",
                    lazy: () => import("src/modules/patients/container/PatientInactiveForm")
                  },
                  {
                    path: ":patientId/pause",
                    lazy: () => import("src/modules/patients/container/PatientPauseForm")
                  }
                ]
              },
              {
                path: ":id",
                lazy: () => import("src/modules/patients/layout/PatientFormLayout"),
                children: [
                  {
                    path: "devices",
                    children: [
                      {
                        index: true,
                        lazy: () => import("src/modules/patients/modules/connections/container/Connections")
                      },
                      {
                        path: "new",
                        lazy: () => import("src/modules/patients/modules/connections/container/ConnectionDialogForm")
                      }
                      ,
                      {
                        path: "orders",
                        lazy: () => import("src/modules/patients/modules/orders/container/Orders")
                      },
                      {
                        path: "groups",
                        lazy: () => import("src/modules/patients/modules/groups/container/Groups"),
                        children: [
                          {
                            path: ":groupId",
                            lazy: () => import("src/modules/patients/modules/groups/container/GroupDialog")
                          }
                        ]
                      }
                    ]
                  },
                  {
                    path: "invasive/cieds",
                    lazy: () => import("src/modules/patients/modules/cieds/container/CIEDs"),
                    children: [
                      {
                        path: ":ciedId",
                        lazy: () => import("src/modules/patients/modules/cieds/container/CIEDForm")
                      }
                    ]
                  },
                  {
                    path: "drugs",
                    children: [
                      {
                        path: "all",
                        lazy: () => import("src/modules/patients/modules/drugs/container/DrugsAssign"),
                        children: [
                          {
                            path: "new",
                            handle: {isNew: true},
                            lazy: () => import("src/modules/system/modules/drugs/container/DrugForm")
                          },
                          {
                            path: ":drugId",
                            handle: {isNew: true},
                            lazy: () => import("src/modules/patients/modules/drugs/container/DrugForm")
                          }
                        ]
                      },
                      {
                        path: "intakes",
                        lazy: () => import("src/modules/patients/modules/drugs/modules/intakes/container/Intakes"),
                        children: [
                          {
                            path: "new",
                            lazy: () => import("src/modules/patients/modules/drugs/modules/intakes/container/IntakeForm")
                          }
                        ]
                      },
                      {
                        path: "pzn",
                        lazy: () => import("src/modules/patients/modules/drugs/container/PznDrugAssign"),
                        children: [
                          {
                            path: ":drugId/new",
                            handle: {isNew: true, isPzn: true},
                            lazy: () => import("src/modules/patients/modules/drugs/container/DrugForm")
                          }
                        ]
                      },
                      {
                        index: true,
                        lazy: () => import("src/modules/patients/modules/drugs/container/Drugs")
                      },
                      {
                        lazy: () => import("src/modules/patients/modules/drugs/container/Drugs"),
                        children: [
                          {
                            path: "plan",
                            lazy: () => import("src/modules/patients/modules/drugs/container/PlanSettingsForm")
                          },
                          {
                            path: ":patientDrugId",
                            lazy: () => import("src/modules/patients/modules/drugs/container/DrugForm")
                          }
                        ]
                      }
                    ]
                  },
                  {
                    path: "events/calls",
                    lazy: () => import("src/modules/patients/modules/notices/container/PhoneCalls")
                  },
                  {
                    path: "events",
                    lazy: () => import("src/modules/patients/modules/notices/container/PatientNotices"),
                    children: [
                      {
                        path: "new",
                        lazy: () => import("src/modules/patients/modules/notices/container/PatientNoticeForm")
                      },
                      {
                        path: ":eventId",
                        lazy: () => import("src/modules/patients/modules/notices/container/PatientNoticeForm")
                      }
                    ]
                  },
                  {
                    path: "export",
                    lazy: () => import("src/modules/patients/modules/export/layout/PatientLayout")
                  },
                  {
                    path: "findings",
                    lazy: () => import("src/modules/patients/modules/findings/container/Findings"),
                    children: [
                      {
                        path: ":findingId/medium/:mediumId/bitmarck/transmission",
                        lazy: () => import("src/modules/patients/modules/findings/modules/container/BitmarckTransmissionDetail")
                      },
                      {
                        path: ":findingId/files/:fileId/preview",
                        lazy: () => import("src/modules/patients/modules/findings/container/FilePreview")
                      },
                      {
                        path: ":findingId",
                        lazy: () => import("src/modules/patients/modules/findings/container/FindingForm")
                      }
                    ]
                  },
                  {
                    path: "general",
                    lazy: () => import("src/modules/patients/container/PatientFormTabs"),
                    children: [

                      {
                        path: "subscriptions",
                        children: [
                          {
                            path: "new",
                            lazy:  () => import("src/modules/patients/container/tabs/patient_subscription/PatientSubscriptionForm")
                          },
                          {
                            path: "end",
                            children: [
                              {
                                path: ":subscriptionId",
                                lazy: () => import("src/modules/patients/container/tabs/patient_subscription/PatientSubscriptionEndDialogForm")
                              }
                            ]
                          }
                        ]
                      },
                      {
                        path: "call_logging",
                        lazy: () => import("src/modules/call_logging/container/CallLoggerDialog")
                      },
                      {
                        path: "family_members/:familyMemberId",
                        lazy: () => import("src/modules/patients/container/FamilyMemberForm")
                      },
                      {
                        path: ":taskId/task",
                        lazy: () => import("src/modules/tasks/container/TaskForm")
                      },
                      {
                        path: "tmhi_configuration/:configurationId/summary",
                        lazy: () => import("./modules/patients/container/tmhi_admission/TmhiConfigurationForm")
                      },
                      {
                        path: "tmhi_configuration/:configurationId/monitoring",
                        lazy: () => import("./modules/patients/container/tmhi_admission/TmhiConfigurationForm")
                      },
                      {
                        path: "tmhi_admissions/:tmhiAdmissionId",
                        lazy: () => import("./modules/patients/container/tmhi_admission/TmhiAdmissionForm")
                      }
                    ]
                  },
                  {
                    path: "health_providers",
                    lazy: () => import("src/modules/patients/modules/health_providers/container/PatientHealthProviders"),
                    children: [
                      {
                        path: ":patientHealthProviderId",
                        lazy: () => import("src/modules/patients/modules/health_providers/components/PatientHealthProviderForm")
                      }
                    ]
                  },
                  {
                    path: "histories",
                    lazy: () => import("src/modules/patients/modules/histories/container/Histories"),
                    children: [
                      {
                        path: ":recordId",
                        lazy: () => import("src/modules/patients/modules/histories/components/HistoryDialog"),
                        children: [
                          {
                            path: "patient/:id/measurement/:measurementId",
                            lazy: () => import("src/modules/patients/modules/measurements/container/MeasurementDetail")
                          }
                        ]
                      }
                    ]
                  },
                  {
                    path: "insights",
                    lazy: () => import("src/modules/patients/container/PatientInsight"),
                    children: [
                      {
                        path: "histories",
                        children: [
                          {
                            path: ":recordId",
                            lazy: () => import("src/modules/patients/modules/histories/components/HistoryDialog"),
                            children: [
                              {
                                path: "patient/:id/measurement/:measurementId",
                                lazy: () => import("src/modules/patients/modules/measurements/container/MeasurementDetail")
                              }
                            ]
                          }
                        ]
                      }

                    ]
                  },
                  {
                    path: "measurements",
                    lazy: () => import("src/modules/patients/modules/measurements/container/Measurements"),
                    children: [
                      {
                        path: ":measurementId",
                        lazy: () => import("src/modules/patients/modules/measurements/container/MeasurementDetail")
                      }
                    ]
                  },
                  {
                    path: "measurements",
                    children: [
                      {
                        path: "ecgs",
                        lazy: () => import("src/modules/patients/modules/measurements/modules/ecg/container/ECGs"),
                        children: [
                          {
                            path: ":ecgId",
                            lazy: () => import("src/modules/patients/modules/measurements/modules/ecg/container/ECGDetail"),
                            children: [
                              {
                                path: ":taskId/task",
                                lazy: () => import("src/modules/tasks/container/TaskForm")
                              }
                            ]
                          }
                        ]
                      },
                      {
                        path: "ecg_triggers",
                        lazy: () => import("src/modules/patients/modules/triggers/container/EcgTriggers"),
                        children: [
                          {
                            path: ":triggerId",
                            lazy: () => import("src/modules/patients/modules/triggers/container/EcgTriggerForm")
                          },
                          {
                            path: ":triggerId",
                            children: [
                              {
                                path: "edit",
                                lazy: () => import("src/modules/patients/modules/triggers/container/EcgTriggerForm")
                              },
                              {
                                path: "events",
                                lazy: () => import("src/modules/patients/modules/triggers/modules/events/container/EcgEvents")
                              }
                            ]
                          }
                        ]
                      },
                      {
                        path: "observation_cases",
                        lazy: () => import("src/modules/patients/modules/observation_cases/container/ObservationCases"),
                        children: [
                          {
                            path: ":observationCaseId",
                            lazy: () => import("src/modules/dashboard/modules/triggers/container/MeasurementEventsDialog"),
                            children: [
                              {
                                path: "task/:taskId",
                                lazy: () => import("src/modules/tasks/container/TaskForm")
                              }
                            ]
                          }
                        ]
                      },
                      {
                        path: "triggers",
                        lazy: () => import("src/modules/patients/modules/triggers/container/Triggers"),
                        children: [
                          {
                            path: ":triggerId",
                            lazy: () => import("src/modules/patients/modules/triggers/container/TriggerForm")
                          },
                          {
                            path: ":triggerId",
                            children: [
                              {
                                path: "adherence_events",
                                lazy: () => import("src/modules/patients/modules/triggers/modules/events/container/AdherenceEvents")
                              },
                              {
                                path: "edit",
                                lazy: () => import("src/modules/patients/modules/triggers/container/TriggerForm")
                              },
                              {
                                path: "events",
                                lazy: () => import("src/modules/patients/modules/triggers/modules/events/container/Events")
                              }
                            ]
                          }
                        ]
                      }
                    ]
                  },
                  {
                    path: "programs",
                    children: [
                      {
                        index: true,
                        lazy: () => import("src/modules/patients/modules/programs/container/Programs")
                      },
                      {
                        lazy: () => import("src/modules/patients/modules/programs/container/Programs"),
                        children: [
                          {
                            path: "new/:programId",
                            lazy: () => import("src/modules/patients/modules/programs/container/ProgramAssign")
                          }
                        ]
                      },
                      {
                        path: ":programId",
                        lazy: () => import("src/modules/patients/modules/programs/container/CurrentProgram"),
                        children: [
                          {
                            path: 'report',
                            lazy: () => import("src/modules/patients/modules/programs/container/ReportDialog")
                          }
                        ]
                      },
                      {
                        path: ":patientProgramId",
                        children: [
                          {
                            path: "goals",
                            lazy: () => import("src/modules/patients/modules/programs/modules/goals/container/Goals"),
                            children: [
                              {
                                path: ":goalId",
                                lazy: () => import("src/modules/patients/modules/programs/modules/goals/container/GoalForm")
                              },
                              {
                                path: "task/:taskId",
                                lazy: () => import("src/modules/tasks/container/TaskForm")
                              }
                            ]
                          },
                          {
                            path: "interview_guides",
                            lazy: () => import("src/modules/patients/modules/programs/modules/interview_guides/container/InterviewGuides"),
                            children: [
                              {
                                path: ":interviewGuideId",
                                lazy: () => import("src/modules/patients/modules/programs/modules/interview_guides/container/InterviewGuideDialog")
                              }
                            ]
                          },
                          {
                            path: "lifestyles",
                            lazy: () => import("src/modules/patients/modules/programs/modules/lifestyles/container/Lifestyles")
                          },
                          {
                            path: "program_components",
                            lazy: () => import("src/modules/patients/modules/programs/modules/program_components/container/ProgramComponents"),
                            children: [
                              {
                                path: ":programComponentId",
                                lazy: () => import("src/modules/patients/modules/programs/modules/program_components/container/ProgramComponentForm")
                              },
                              {
                                path: "details/:programComponentId",
                                lazy: () =>
                                  import("src/modules/patients/modules/programs/modules/program_components/container/ProgramComponentDetails")
                              }
                            ]
                          },
                          {
                            path: "symptoms",
                            lazy: () => import("src/modules/patients/modules/programs/modules/symptoms/container/Symptoms")
                          }
                        ]
                      }
                    ]
                  },
                  {
                    path: "studies",
                    children: [
                      {
                        index: true,
                        lazy: () => import("src/modules/patients/modules/studies/container/Studies")
                      },
                      {
                        path: "new",
                        lazy: () => import("src/modules/patients/modules/studies/container/AssignStudy")
                      },
                      {
                        path: ":patientStudyId",
                        lazy: () => import("src/modules/patients/modules/studies/container/PatientStudy")
                      }
                    ]
                  },
                  {
                    path: "surveys",
                    loader: () => ({breadcrumb: <FormattedMessage id="patients.layout.surveys" defaultMessage="Surveys"/>}),
                    children: [
                      {
                        index: true,
                        lazy: () => import("src/modules/patients/modules/surveys/container/Surveys")
                      },
                      {
                        path: "new",
                        lazy: () => import("src/modules/patients/modules/surveys/container/SurveyForm")
                      },
                      {
                        path: "triggers",
                        lazy: () => import("src/modules/patients/modules/surveys/container/Triggers"),
                        children: [
                          {
                            path: ":triggerId",
                            children: [
                              {
                                index: true,
                                lazy: () => import("src/modules/patients/modules/surveys/container/TriggerForm")
                              },
                              {
                                path: "adherence_events",
                                lazy: () => import("src/modules/patients/modules/surveys/container/AdherenceEvents")
                              },
                              {
                                path: "edit",
                                lazy: () => import("src/modules/patients/modules/surveys/container/TriggerForm")
                              },
                              {
                                path: "events",
                                lazy: () => import("src/modules/patients/modules/surveys/container/Events")
                              }
                            ]
                          }
                        ]
                      },
                      {
                        path: ":patientSurveyId",
                        lazy: () => import("src/modules/patients/modules/surveys/sessions/container/Sessions"),
                        children: [
                          {
                            path: ":patientSurveySessionId",
                            lazy: () => import("src/modules/patients/modules/surveys/sessions/components/SessionDialog")
                          }
                        ]
                      }
                    ]
                  },
                  {
                    path: "tasks",
                    lazy: () => import("src/modules/patients/modules/tasks/container/LinkedTasks"),
                    children: [
                      {
                        path: ":taskId",
                        lazy: () => import("src/modules/tasks/container/TaskForm")
                      },
                      {
                        path: ":taskId/work",
                        lazy: () => import("src/modules/tasks/container/TaskWork")
                      },
                      {
                        path: "bulk/assign",
                        lazy: () => import("src/modules/tasks/container/BulkAssign")
                      },
                      {
                        path: "patient/:id/ecg/:ecgId",
                        lazy: () => import("src/modules/patients/modules/measurements/modules/ecg/container/ECGDetail"),
                        children: [
                          {
                            path: ":taskId/task",
                            lazy: () => import("src/modules/tasks/container/TaskForm")
                          }
                        ]
                      }
                    ]
                  },
                  {
                    path: "tasks/patient",
                    lazy: () => import("src/modules/patients/modules/tasks/container/Tasks"),
                    children: [
                      {
                        path: ":taskId",
                        lazy: () => import("src/modules/tasks/container/TaskForm")
                      },
                      {
                        //TODO(Keune): Is this even used?
                        path: ":taskId/work",
                        lazy: () => import("src/modules/tasks/container/TaskWork")
                      }
                    ]
                  },
                  {
                    path: "treatments",
                    lazy: () => import("src/modules/patients/modules/treatments/container/Treatments"),
                    children: [
                      {
                        path: ":treatmentId",
                        lazy: () => import("src/modules/patients/modules/treatments/container/TreatmentForm")
                      }
                    ]
                  },
                  {
                    path: "icd10s",
                    lazy: () => import("src/modules/patients/modules/icd10s/container/Icd10s"),
                    children: [
                      {
                        path: ":patientIcd10Id",
                        lazy: () => import("src/modules/patients/modules/icd10s/container/Icd10Form")
                      }
                    ]
                  },
                  {
                    path: "visite",
                    children: [
                      {
                        index: true,
                        lazy: () => import("src/modules/patients/modules/visite/container/Visite")
                      },
                      {
                        path: "seen",
                        lazy: () => import("src/modules/patients/modules/visite/container/VisiteSeen")
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            path: "system",
            lazy: () => import("src/modules/system/layout/SystemLayout"),
            ErrorBoundary: SaniQErrorBoundary,
            children: [
              {
                path: "user/:id",
                lazy: () => import("src/modules/system/modules/users/container/UserFormDialog")
              },
              {
                path: "account",
                lazy: () => import("src/modules/system/modules/account/container/Account")
              },
              {
                path: "account/browsers",
                lazy: () => import("src/modules/system/modules/browsers/container/Browsers")
              },
              {
                path: "export",
                lazy: () => import("src/modules/patients/modules/export/container/Results")
              },
              {
                path: "settings/legals",
                lazy: () => import("src/modules/system/modules/legals/container/Legals")
              },
              {
                path: "settings/payments",
                lazy: () => import("src/modules/system/modules/payment_profile/container/PaymentProfile")
              },
              {
                path: "settings/patient_subscriptions",
                lazy: () => import("src/modules/system/modules/subscriptions/container/PatientSubscriptions")
              },
              {
                path: "sensors",
                lazy: () => import("src/modules/system/modules/sensors/container/Sensors")
              },
              {
                path: "settings",
                lazy: () => import("src/modules/system/layout/SystemSettingsLayout")
              },
              {
                path: "tmhi",
                lazy: () => import("src/modules/system/modules/tmhi/layout/TmhiLayout")
              },
              {
                path: "users",
                lazy: () => import("src/modules/system/modules/users/container/Users"),
                children: [
                  {
                    path: ":id",
                    lazy: () => import("src/modules/system/modules/users/container/UserFormDialog")
                  }
                ]
              },
              {
                path: "users/roles",
                lazy: () => import("src/modules/system/modules/roles/container/Roles"),
                children: [
                  {
                    path: ":id",
                    lazy: () => import("src/modules/system/modules/roles/container/RoleForm")
                  }
                ]
              },
              {
                path: "webhooks",
                lazy: () => import("src/modules/system/modules/webhooks/components/WebhookEndpoints"),
                children: [
                  {
                    path: ":webhookEndpointId",
                    lazy: () => import("src/modules/system/modules/webhooks/components/WebhookEndpointForm")
                  }
                ]
              },
              {
                path: "webhooks/events",
                lazy: () => import("src/modules/system/modules/webhooks/components/WebhookEndpointEvents"),
                children: [
                  {
                    path: ":id",
                    lazy: () => import("src/modules/system/modules/webhooks/components/WebhookEventDetail")
                  }
                ]
              },
              {
                path: "settings/integrations",
                lazy: () => import("src/modules/system/modules/integrations/components/Integrations"),
                children: [
                  {
                    path: 'hl7',
                    lazy: () => import("src/modules/system/modules/integrations/container/detail/HL7Detail")
                  },
                  {
                    path: 'microport',
                    lazy: () => import("src/modules/system/modules/integrations/container/detail/MicroportDetail")
                  },
                  {
                    path: 'biotronik',
                    lazy: () => import("src/modules/system/modules/integrations/container/detail/BiotronikDetail")
                  },
                  {
                    path: 'bitmarck',
                    lazy: () => import("src/modules/system/modules/integrations/container/detail/BitmarckDetail")
                  },
                  {
                    path: 'therafox',
                    lazy: () => import("src/modules/system/modules/integrations/container/detail/TherafoxDetail")
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        lazy: () => import("src/modules/password/layout/PasswordLayout"),
        children: [
          {
            path: "unlock",
            lazy: () => import("src/modules/unlock/containers/Unlock")
          }
        ]
      }
    ]
  }
]);

//FIXME(Keune): ErrorBoundary all in IntlProvider Context
root.render(
  <Provider store={store}>
    <RouterProvider router={router}/>
  </Provider>
);
